import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUsers } from '@/features/org/queries';
import { errorMessage } from '@/lib/api';
import { useAvailableNumbers, useCreateJob } from './queries';

const schema = z.object({
  name: z.string().trim().min(1, 'Give the job a name').max(120),
  description: z.string().trim().max(2000).optional(),
  senderLabel: z.string().trim().max(40).optional(),
  phoneMode: z.enum(['none', 'existing', 'provision']),
  phoneNumberId: z.string().optional(),
  areaCode: z.string().regex(/^\d{3}$|^$/, 'Use a 3-digit area code').optional(),
  adminUserIds: z.array(z.string()),
});
type Values = z.infer<typeof schema>;

export function NewJobDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const create = useCreateJob();
  const numbers = useAvailableNumbers(open);
  const users = useUsers(open);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '', senderLabel: '', phoneMode: 'provision', phoneNumberId: undefined, areaCode: '', adminUserIds: [] },
  });
  const phoneMode = form.watch('phoneMode');
  const senderLabel = form.watch('senderLabel');

  useEffect(() => {
    if (!open) {
      form.reset();
      setError(null);
    }
  }, [open, form]);

  useEffect(() => {
    if (numbers.data && numbers.data.items.length === 0 && phoneMode === 'existing') form.setValue('phoneMode', 'provision');
  }, [numbers.data, phoneMode, form]);

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    if (v.phoneMode === 'existing' && !v.phoneNumberId) {
      form.setError('phoneNumberId', { message: 'Choose a number' });
      return;
    }
    try {
      const res = await create.mutateAsync({
        name: v.name,
        description: v.description || undefined,
        senderLabel: v.senderLabel || undefined,
        phoneNumberId: v.phoneMode === 'existing' ? v.phoneNumberId : undefined,
        provisionNumber: v.phoneMode === 'provision' ? { areaCode: v.areaCode || undefined } : undefined,
        adminUserIds: v.adminUserIds.length ? v.adminUserIds : undefined,
      });
      toast.success(`Job "${res.job.name}" created`);
      onOpenChange(false);
      navigate(`/jobs/${res.job.id}`);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  const activeUsers = (users.data?.items ?? []).filter((u) => u.status === 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New job</DialogTitle>
          <DialogDescription>A job is a self-contained workspace with its own phone number, contacts, and team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-5" noValidate>
          <FormError message={error} />
          <Field label="Job name" htmlFor="name" required error={form.formState.errors.name?.message}>
            <Input id="name" autoFocus placeholder="e.g. Orlando Tower – Phase 2" {...form.register('name')} />
          </Field>
          <Field label="Description" htmlFor="description" error={form.formState.errors.description?.message}>
            <Textarea id="description" rows={2} placeholder="Optional. Where, what, who." {...form.register('description')} />
          </Field>
          <Field
            label="Sender label"
            htmlFor="senderLabel"
            description={
              senderLabel ? (
                <>
                  Messages will start with <span className="text-foreground font-medium">{senderLabel}: </span>
                </>
              ) : (
                'Optional short prefix so recipients know who is texting, e.g. "Orlando Tower".'
              )
            }
            error={form.formState.errors.senderLabel?.message}
          >
            <Input id="senderLabel" maxLength={40} {...form.register('senderLabel')} />
          </Field>

          <div className="grid gap-3">
            <Label>Phone number</Label>
            <Controller
              control={form.control}
              name="phoneMode"
              render={({ field }) => (
                <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2">
                  <label className="flex items-start gap-3 border p-3 has-[[data-state=checked]]:border-primary">
                    <RadioGroupItem value="provision" className="mt-0.5" />
                    <div className="grid gap-1">
                      <span className="text-sm font-medium">Get a new number</span>
                      <span className="text-muted-foreground text-xs">A dedicated number is provisioned for this job.</span>
                      {phoneMode === 'provision' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Label htmlFor="areaCode" className="text-muted-foreground text-xs font-normal">
                            Preferred area code
                          </Label>
                          <Input id="areaCode" className="h-8 w-24" placeholder="407" inputMode="numeric" maxLength={3} {...form.register('areaCode')} />
                        </div>
                      )}
                      {form.formState.errors.areaCode && <span className="text-destructive text-xs">{form.formState.errors.areaCode.message}</span>}
                    </div>
                  </label>
                  <label className="flex items-start gap-3 border p-3 has-[[data-state=checked]]:border-primary aria-disabled:opacity-50" aria-disabled={numbers.data?.items.length === 0}>
                    <RadioGroupItem value="existing" className="mt-0.5" disabled={numbers.data?.items.length === 0} />
                    <div className="grid flex-1 gap-1">
                      <span className="text-sm font-medium">Use an available number</span>
                      <span className="text-muted-foreground text-xs">
                        {numbers.isPending ? 'Loading…' : `${numbers.data?.items.length ?? 0} unassigned number(s) in your organization.`}
                      </span>
                      {phoneMode === 'existing' && (
                        <Controller
                          control={form.control}
                          name="phoneNumberId"
                          render={({ field: f }) => (
                            <Select value={f.value} onValueChange={f.onChange}>
                              <SelectTrigger className="mt-2 w-full" aria-invalid={!!form.formState.errors.phoneNumberId}>
                                <SelectValue placeholder="Choose a number" />
                              </SelectTrigger>
                              <SelectContent>
                                {(numbers.data?.items ?? []).map((n) => (
                                  <SelectItem key={n.id} value={n.id}>
                                    {n.display} {n.friendlyName ? `· ${n.friendlyName}` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      )}
                      {form.formState.errors.phoneNumberId && <span className="text-destructive text-xs">{form.formState.errors.phoneNumberId.message}</span>}
                    </div>
                  </label>
                  <label className="flex items-start gap-3 border p-3 has-[[data-state=checked]]:border-primary">
                    <RadioGroupItem value="none" className="mt-0.5" />
                    <div className="grid gap-1">
                      <span className="text-sm font-medium">Assign later</span>
                      <span className="text-muted-foreground text-xs">The job cannot send messages until a number is assigned.</span>
                    </div>
                  </label>
                </RadioGroup>
              )}
            />
          </div>

          <div className="grid gap-2">
            <Label>Job admins</Label>
            <p className="text-muted-foreground -mt-1 text-xs">Admins can send messages and manage the team. You can invite people later from the Team tab.</p>
            <Controller
              control={form.control}
              name="adminUserIds"
              render={({ field }) => (
                <ScrollArea className="max-h-40 border">
                  <div className="divide-y">
                    {users.isPending && <p className="text-muted-foreground p-3 text-xs">Loading users…</p>}
                    {activeUsers.map((u) => {
                      const checked = field.value.includes(u.id);
                      return (
                        <label key={u.id} className="hover:bg-accent/50 flex cursor-pointer items-center gap-3 px-3 py-2">
                          <Checkbox checked={checked} onCheckedChange={(v) => field.onChange(v ? [...field.value, u.id] : field.value.filter((id) => id !== u.id))} />
                          <span className="text-sm">{u.fullName}</span>
                          <span className="text-muted-foreground ml-auto truncate text-xs">{u.email}</span>
                        </label>
                      );
                    })}
                    {!users.isPending && activeUsers.length === 0 && <p className="text-muted-foreground p-3 text-xs">No users yet.</p>}
                  </div>
                </ScrollArea>
              )}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create job'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
